// Messagerie Unifiée - Réseau Social AbracadaWorld
// Connecte tous les messages : Shop, Cockpit Pro, Cockpit Particulier
// Style Instagram DM - Optimisé Mobile (iOS, Android, Samsung, Xiaomi, etc.)

import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Send, MoreVertical, Phone, Video, Info, Image, Paperclip,
  Check, CheckCheck, Clock, Star, Archive, Trash2, ChevronLeft,
  Store, User, Package, Briefcase, Megaphone, BadgeCheck, Camera,
  Mic, Smile, Heart, Plus, Settings, Bell, ArrowLeft, Ban, Flag,
  ShieldOff, AlertTriangle, X, Filter
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { TrustBadgeIcon } from '../../components/badges/TrustBadge';
import { FounderBadgeIcon } from '../../components/badges/FounderBadge';

// ══════════════════════════════════════════════════════════════════════════════
// DONNÉES DÉMO - CONVERSATIONS UNIFIÉES (Shop + Cockpit Pro + Cockpit Particulier)
// ══════════════════════════════════════════════════════════════════════════════

// Production: conversations vides, remplies par l'API
const DEMO_CONVERSATIONS = [];

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT: Filtres Premium Horizontaux Scrollables
// ══════════════════════════════════════════════════════════════════════════════

const FILTER_OPTIONS = [
  { id: 'all', label: 'Tous', icon: null, count: null },
  { id: 'unread', label: 'Non lus', icon: Bell, count: 'unread' },
  { id: 'friends', label: 'Mes amis', icon: Heart, count: 'friends' },
  { id: 'pros', label: 'Mes pros', icon: Briefcase, count: 'pros' },
  { id: 'products', label: 'Produits', icon: Package, count: 'products' },
  { id: 'services', label: 'Prestations', icon: Briefcase, count: 'services' },
  { id: 'annonces', label: 'Annonces', icon: Megaphone, count: 'annonces' },
];

function PremiumFilters({ filter, setFilter, conversations }) {
  const scrollRef = useRef(null);
  
  // Calculer les compteurs
  const counts = {
    unread: conversations.filter(c => c.unread > 0).length,
    friends: conversations.filter(c => c.isFriend).length,
    pros: conversations.filter(c => c.isPro).length,
    products: conversations.filter(c => c.type === 'product').length,
    services: conversations.filter(c => c.type === 'service').length,
    annonces: conversations.filter(c => c.type === 'annonce').length,
  };
  
  return (
    <div 
      ref={scrollRef}
      className="flex gap-2 overflow-x-auto scrollbar-hide px-4 py-3"
      style={{
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
    >
      {FILTER_OPTIONS.map((option) => {
        const isActive = filter === option.id;
        const count = option.count ? counts[option.count] : null;
        const Icon = option.icon;
        
        return (
          <motion.button
            key={option.id}
            whileTap={{ scale: 0.95 }}
            onClick={() => setFilter(option.id)}
            className={`
              flex items-center gap-2 px-4 py-2.5 rounded-full whitespace-nowrap
              text-sm font-semibold transition-all duration-200
              flex-shrink-0 min-w-fit
              ${isActive 
                ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/30' 
                : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }
            `}
            style={{
              boxShadow: isActive ? '0 4px 14px rgba(249, 115, 22, 0.35)' : undefined,
            }}
          >
            {Icon && (
              <Icon 
                size={16} 
                className={isActive ? 'text-white' : 'text-gray-500'}
              />
            )}
            <span>{option.label}</span>
            {count !== null && count > 0 && (
              <span 
                className={`
                  min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold
                  flex items-center justify-center
                  ${isActive 
                    ? 'bg-white/25 text-white' 
                    : 'bg-orange-100 text-orange-600'
                  }
                `}
              >
                {count}
              </span>
            )}
          </motion.button>
        );
      })}
      
      {/* Spacer pour le dernier élément */}
      <div className="w-4 flex-shrink-0" />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT: Liste des conversations
// ══════════════════════════════════════════════════════════════════════════════

function ConversationList({ conversations, selectedId, onSelect, filter, setFilter }) {
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = conversations.filter(c => {
    const matchesSearch = c.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (c.context?.title?.toLowerCase().includes(searchQuery.toLowerCase()));
    
    if (filter === 'all') return matchesSearch;
    if (filter === 'unread') return matchesSearch && c.unread > 0;
    if (filter === 'friends') return matchesSearch && c.isFriend;
    if (filter === 'pros') return matchesSearch && c.isPro;
    if (filter === 'products') return matchesSearch && c.type === 'product';
    if (filter === 'services') return matchesSearch && c.type === 'service';
    if (filter === 'annonces') return matchesSearch && c.type === 'annonce';
    return matchesSearch;
  });

  const getContextIcon = (type) => {
    switch (type) {
      case 'product': return <Package size={12} className="text-blue-500" />;
      case 'service': return <Briefcase size={12} className="text-emerald-500" />;
      case 'annonce': return <Megaphone size={12} className="text-pink-500" />;
      default: return <User size={12} className="text-gray-400" />;
    }
  };

  const getContextColor = (type) => {
    switch (type) {
      case 'product': return 'bg-blue-50 text-blue-600';
      case 'service': return 'bg-emerald-50 text-emerald-600';
      case 'annonce': return 'bg-pink-50 text-pink-600';
      default: return 'bg-gray-50 text-gray-600';
    }
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header - Safe area top pour iOS */}
      <div 
        className="border-b border-gray-100"
        style={{ paddingTop: 'max(16px, env(safe-area-inset-top))' }}
      >
        <div className="px-4 pb-3">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-xl font-bold text-gray-900">Messages</h1>
            <div className="flex items-center gap-1">
              <button className="p-2.5 hover:bg-gray-100 rounded-full transition-colors">
                <Filter size={20} className="text-gray-500" />
              </button>
              <button className="p-2.5 hover:bg-gray-100 rounded-full transition-colors">
                <Settings size={20} className="text-gray-500" />
              </button>
            </div>
          </div>
          
          {/* Search - Optimisé tactile */}
          <div className="relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-gray-100 border-0 rounded-2xl focus:ring-2 focus:ring-orange-500 text-base"
              style={{ fontSize: '16px' }} // Empêche le zoom sur iOS
            />
          </div>
        </div>
        
        {/* Filtres Premium Scrollables */}
        <PremiumFilters 
          filter={filter} 
          setFilter={setFilter} 
          conversations={conversations}
        />
      </div>

      {/* Conversations List - Optimisé scroll natif */}
      <div 
        className="flex-1 overflow-y-auto overscroll-contain"
        style={{
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <Search size={24} className="text-gray-400" />
            </div>
            <p className="font-medium">Aucune conversation</p>
            <p className="text-sm mt-1">Commence à discuter avec la communauté !</p>
          </div>
        ) : (
          filtered.map((conv) => (
            <motion.button
              key={conv.id}
              whileTap={{ scale: 0.98, backgroundColor: 'rgba(249, 115, 22, 0.05)' }}
              onClick={() => onSelect(conv)}
              className={`w-full p-4 flex items-start gap-3 border-b border-gray-50 active:bg-orange-50 transition-colors ${
                selectedId === conv.id ? 'bg-orange-50' : ''
              }`}
            >
              {/* Avatar - Taille optimisée tactile */}
              <div className="relative flex-shrink-0">
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-lg"
                  style={{ backgroundColor: conv.userColor }}
                >
                  {conv.userName.charAt(0)}
                </div>
                {conv.isOnline && (
                  <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 border-[3px] border-white rounded-full" />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 text-left">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-gray-900 truncate">{conv.userName}</h4>
                  {conv.verified && <BadgeCheck size={14} className="text-blue-500 fill-blue-500 flex-shrink-0" />}
                  {conv.is_founder && <FounderBadgeIcon founderNumber={conv.founder_number} size={14} />}
                  {conv.trust_level && <TrustBadgeIcon trustLevel={conv.trust_level} size={14} />}
                  <span className="text-xs text-gray-400 ml-auto flex-shrink-0">{conv.lastMessageTime}</span>
                </div>
                
                {/* Context badge */}
                {conv.context && (
                  <div className="flex items-center gap-1.5 mb-1">
                    {getContextIcon(conv.type)}
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${getContextColor(conv.type)}`}>
                      {conv.context.type}
                    </span>
                    <span className="text-xs text-gray-500 truncate">{conv.context.title}</span>
                  </div>
                )}
                
                <p className={`text-sm truncate ${conv.unread ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                  {conv.lastMessage}
                </p>
              </div>

              {/* Unread badge */}
              {conv.unread > 0 && (
                <span className="w-6 h-6 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center justify-center flex-shrink-0">
                  {conv.unread}
                </span>
              )}
            </motion.button>
          ))
        )}
      </div>
      
      {/* Badge DÉMO */}
      <div className="p-3 border-t border-gray-100 bg-gray-50">
        <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
          <span className="bg-black/10 px-2 py-0.5 rounded font-semibold">DÉMO</span>
          <span>Messagerie unifiée Shop + Cockpit</span>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT: Modal Bannir / Signaler utilisateur
// ══════════════════════════════════════════════════════════════════════════════

function BanUserModal({ user, isOpen, onClose, onBan, onReport }) {
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] bg-black/60 flex items-end justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md bg-white rounded-t-3xl overflow-hidden"
          style={{ paddingBottom: 'max(20px, env(safe-area-inset-bottom))' }}
        >
          {/* Handle */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>
          
          {/* Header */}
          <div className="px-6 pb-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                style={{ backgroundColor: user?.userColor }}
              >
                {user?.userName?.charAt(0)}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{user?.userName}</h3>
                <p className="text-sm text-gray-500">Gérer cette personne</p>
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <div className="p-2">
            {/* Signaler */}
            <button
              onClick={() => {
                onReport();
                onClose();
              }}
              className="w-full flex items-center gap-4 p-4 rounded-2xl hover:bg-gray-50 active:bg-gray-100 transition-colors"
            >
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Flag size={22} className="text-amber-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-gray-900">Signaler</p>
                <p className="text-sm text-gray-500">Signaler un comportement inapproprié</p>
              </div>
            </button>
            
            {/* Bloquer */}
            <button
              onClick={() => {
                onBan('block');
                onClose();
              }}
              className="w-full flex items-center gap-4 p-4 rounded-2xl hover:bg-gray-50 active:bg-gray-100 transition-colors"
            >
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                <ShieldOff size={22} className="text-orange-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-gray-900">Bloquer</p>
                <p className="text-sm text-gray-500">Cette personne ne pourra plus te contacter</p>
              </div>
            </button>
            
            {/* Bannir définitivement */}
            <button
              onClick={() => {
                onBan('ban');
                onClose();
              }}
              className="w-full flex items-center gap-4 p-4 rounded-2xl hover:bg-red-50 active:bg-red-100 transition-colors"
            >
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <Ban size={22} className="text-red-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-red-600">Bannir définitivement</p>
                <p className="text-sm text-gray-500">Bloquer et supprimer la conversation</p>
              </div>
            </button>
          </div>
          
          {/* Cancel */}
          <div className="px-4 pb-2">
            <button
              onClick={onClose}
              className="w-full py-4 text-center font-semibold text-gray-600 bg-gray-100 rounded-2xl hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT: Zone de chat
// ══════════════════════════════════════════════════════════════════════════════

function ChatArea({ conversation, onSendMessage, onBack, onBanUser }) {
  const [message, setMessage] = useState('');
  const [showBanModal, setShowBanModal] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversation?.messages]);

  const handleSend = () => {
    if (!message.trim()) return;
    onSendMessage(message);
    setMessage('');
    inputRef.current?.focus();
  };
  
  const handleBan = (type) => {
    if (onBanUser) {
      onBanUser(conversation.id, type);
    }
    // Fermer et revenir à la liste
    onBack();
  };
  
  const handleReport = () => {
    // TODO: Implémenter le signalement
    alert('Utilisateur signalé. Merci pour ton retour !');
  };

  if (!conversation) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 p-8">
        <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mb-4">
          <Send size={32} className="text-gray-400" />
        </div>
        <p className="font-semibold text-gray-900">Tes messages</p>
        <p className="text-sm text-gray-500 text-center mt-1">
          Sélectionne une conversation ou démarre une nouvelle discussion
        </p>
      </div>
    );
  }

  const getContextBadge = () => {
    if (!conversation.context) return null;
    
    const colors = {
      product: 'bg-blue-500',
      service: 'bg-emerald-500',
      annonce: 'bg-pink-500',
    };
    
    return (
      <div className={`${colors[conversation.type] || 'bg-gray-500'} text-white text-[10px] font-bold px-2 py-1 rounded-full`}>
        {conversation.context.type}
      </div>
    );
  };

  return (
    <div className="flex-1 flex flex-col h-full bg-white overflow-hidden">
      {/* Header avec avatar, nom et boutons - FIXÉ EN HAUT */}
      <div className="flex-shrink-0 bg-white border-b border-gray-100 flex items-center gap-3 px-4 py-3 z-10">
        <button 
          onClick={onBack}
          className="lg:hidden p-2.5 hover:bg-gray-100 active:bg-gray-200 rounded-full -ml-2 transition-colors"
        >
          <ArrowLeft size={22} className="text-gray-700" />
        </button>
        
        <div 
          className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
          style={{ backgroundColor: conversation.userColor }}
        >
          {conversation.userName.charAt(0)}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900 truncate">{conversation.userName}</h3>
            {conversation.verified && <BadgeCheck size={14} className="text-blue-500 fill-blue-500 flex-shrink-0" />}
            {conversation.is_founder && <FounderBadgeIcon founderNumber={conversation.founder_number} size={14} />}
            {conversation.trust_level && <TrustBadgeIcon trustLevel={conversation.trust_level} size={14} />}
            {getContextBadge()}
          </div>
          <p className="text-xs text-gray-500">
            {conversation.isOnline ? 'En ligne' : 'Hors ligne'}
            {conversation.userMetier && ` • ${conversation.userMetier}`}
          </p>
        </div>
        
        <div className="flex items-center gap-1">
          <button className="p-2.5 hover:bg-gray-100 rounded-full active:bg-gray-200 transition-colors">
            <Phone size={18} className="text-gray-500" />
          </button>
          <button className="p-2.5 hover:bg-gray-100 rounded-full active:bg-gray-200 transition-colors">
            <Video size={18} className="text-gray-500" />
          </button>
          
          {/* Menu Plus - Bannir/Signaler */}
          <div className="relative">
            <button 
              onClick={() => setShowMoreMenu(!showMoreMenu)}
              className="p-2.5 hover:bg-gray-100 rounded-full active:bg-gray-200 transition-colors"
            >
              <MoreVertical size={18} className="text-gray-500" />
            </button>
            
            {/* Dropdown menu */}
            <AnimatePresence>
              {showMoreMenu && (
                <>
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-40"
                    onClick={() => setShowMoreMenu(false)}
                  />
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50"
                  >
                    <button
                      onClick={() => {
                        setShowMoreMenu(false);
                        setShowBanModal(true);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 active:bg-red-100 transition-colors text-left"
                    >
                      <Ban size={18} className="text-red-500" />
                      <div>
                        <p className="font-medium text-red-600">Bloquer / Bannir</p>
                        <p className="text-xs text-gray-500">Gérer cet utilisateur</p>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setShowMoreMenu(false);
                        handleReport();
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-amber-50 active:bg-amber-100 transition-colors text-left border-t border-gray-100"
                    >
                      <Flag size={18} className="text-amber-500" />
                      <div>
                        <p className="font-medium text-amber-600">Signaler</p>
                        <p className="text-xs text-gray-500">Comportement inapproprié</p>
                      </div>
                    </button>
                    <button
                      onClick={() => setShowMoreMenu(false)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 active:bg-gray-100 transition-colors text-left border-t border-gray-100"
                    >
                      <Archive size={18} className="text-gray-500" />
                      <div>
                        <p className="font-medium text-gray-700">Archiver</p>
                        <p className="text-xs text-gray-500">Masquer la conversation</p>
                      </div>
                    </button>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
      
      {/* Modal Bannir */}
      <BanUserModal
        user={conversation}
        isOpen={showBanModal}
        onClose={() => setShowBanModal(false)}
        onBan={handleBan}
        onReport={handleReport}
      />

      {/* Context Card - FIXÉ */}
      {conversation.context && (
        <div className="flex-shrink-0 px-4 py-3 bg-gray-50 border-b border-gray-100">
          <div className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-200">
            {conversation.context.image ? (
              <img 
                src={conversation.context.image} 
                alt="" 
                className="w-12 h-12 rounded-lg object-cover"
              />
            ) : (
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                conversation.type === 'service' ? 'bg-emerald-100' : 'bg-pink-100'
              }`}>
                {conversation.type === 'service' ? (
                  <Briefcase size={20} className="text-emerald-600" />
                ) : (
                  <Megaphone size={20} className="text-pink-600" />
                )}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{conversation.context.title}</p>
              <p className="text-xs text-orange-600 font-semibold">
                {conversation.context.price ? `${conversation.context.price.toFixed(2)} €` : conversation.context.budget}
                {conversation.context.duration && ` • ${conversation.context.duration}`}
              </p>
            </div>
            <Link 
              to={`/shop/${conversation.type}/${conversation.id}`}
              className="text-xs text-blue-600 font-medium hover:underline"
            >
              Voir
            </Link>
          </div>
        </div>
      )}

      {/* Messages - ZONE SCROLLABLE */}
      <div 
        className="flex-1 overflow-y-auto p-4 space-y-4"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {conversation.messages.map((msg, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className={`flex ${msg.isMe ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[75%] ${msg.isMe ? 'order-2' : ''}`}>
              <div
                className={`px-4 py-2.5 rounded-2xl ${
                  msg.isMe
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-br-md'
                    : 'bg-gray-100 text-gray-900 rounded-bl-md'
                }`}
              >
                <p className="text-sm">{msg.text}</p>
              </div>
              <div className={`flex items-center gap-1 mt-1 ${msg.isMe ? 'justify-end' : ''}`}>
                <span className="text-[10px] text-gray-400">{msg.time}</span>
                {msg.isMe && (
                  msg.read 
                    ? <CheckCheck size={12} className="text-blue-500" />
                    : <Check size={12} className="text-gray-400" />
                )}
              </div>
            </div>
          </motion.div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input - FIXÉ EN BAS */}
      <div 
        className="flex-shrink-0 p-4 border-t border-gray-100 bg-white"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 16px)' }}
      >
        <div className="flex items-center gap-2">
          <button className="p-2 hover:bg-gray-100 rounded-full">
            <Camera size={20} className="text-gray-500" />
          </button>
          <button className="p-2 hover:bg-gray-100 rounded-full">
            <Image size={20} className="text-gray-500" />
          </button>
          <button className="p-2 hover:bg-gray-100 rounded-full">
            <Mic size={20} className="text-gray-500" />
          </button>
          
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Écris un message..."
              className="w-full px-4 py-2.5 bg-gray-100 border-0 rounded-full focus:ring-2 focus:ring-orange-500 text-sm pr-10"
            />
            <button className="absolute right-3 top-1/2 -translate-y-1/2">
              <Smile size={20} className="text-gray-400" />
            </button>
          </div>
          
          {message.trim() ? (
            <motion.button
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              onClick={handleSend}
              className="p-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-full"
            >
              <Send size={18} />
            </motion.button>
          ) : (
            <button className="p-2.5 hover:bg-gray-100 rounded-full">
              <Heart size={20} className="text-gray-500" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL: Page Messagerie Réseau
// ══════════════════════════════════════════════════════════════════════════════

export default function MessagerieReseauPage() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState(DEMO_CONVERSATIONS);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [filter, setFilter] = useState('all');

  const totalUnread = conversations.reduce((acc, c) => acc + c.unread, 0);

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    setShowChat(true);
    // Marquer comme lu
    setConversations(prev => prev.map(c => 
      c.id === conv.id ? { ...c, unread: 0 } : c
    ));
  };

  const handleSendMessage = (text) => {
    if (!selectedConversation) return;
    
    const newMessage = {
      text,
      isMe: true,
      time: 'À l\'instant',
      read: false,
    };

    setConversations(prev => prev.map(c => 
      c.id === selectedConversation.id 
        ? { 
            ...c, 
            messages: [...c.messages, newMessage],
            lastMessage: text,
            lastMessageTime: 'À l\'instant'
          } 
        : c
    ));

    setSelectedConversation(prev => ({
      ...prev,
      messages: [...prev.messages, newMessage]
    }));
  };
  
  // Bannir un utilisateur
  const handleBanUser = (conversationId, banType) => {
    // Supprimer la conversation de la liste
    setConversations(prev => prev.filter(c => c.id !== conversationId));
    setSelectedConversation(null);
    setShowChat(false);
    
    // Afficher une notification (en prod, envoyer au backend)
    const action = banType === 'ban' ? 'banni définitivement' : 'bloqué';
    console.log(`Utilisateur ${action}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Main content area - Plein écran sur mobile */}
      <div className="flex-1 flex overflow-hidden">
        {/* Conversations List - Caché sur mobile quand chat ouvert */}
        <div className={`w-full lg:w-96 lg:border-r lg:border-gray-200 flex flex-col ${showChat ? 'hidden lg:flex' : ''}`}>
          <ConversationList
            conversations={conversations}
            selectedId={selectedConversation?.id}
            onSelect={handleSelectConversation}
            filter={filter}
            setFilter={setFilter}
          />
        </div>

        {/* Chat Area */}
        <div className={`flex-1 flex flex-col ${!showChat ? 'hidden lg:flex' : ''}`}>
          <ChatArea
            conversation={selectedConversation}
            onSendMessage={handleSendMessage}
            onBack={() => setShowChat(false)}
            onBanUser={handleBanUser}
          />
        </div>
      </div>
    </div>
  );
}
